import { post, request } from '../api';

/** Mirrors the view types of `apps/api/src/runs/*`; money always crosses the wire as a string. */

export const RUN_STATUSES = ['queued', 'running', 'waiting_approval', 'waiting_review', 'retrying', 'partially_completed', 'completed', 'failed', 'cancelled', 'expired'] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
/** Sortable columns; the key of a sortable table column is exactly the server sort name. */
export const RUN_SORTS = ['startedAt', 'createdAt', 'cost', 'status', 'model'] as const;
export type RunSort = (typeof RUN_SORTS)[number];

export type ArtifactStatus = 'draft' | 'reviewed' | 'approved' | 'superseded';
export type ExternalActionStatus = 'planned' | 'executing' | 'done' | 'failed' | 'rolled_back';
export type ApprovalKind = 'plan' | 'step' | 'external_action' | 'new_agent' | 'knowledge';
export type ApprovalDecision = 'approved' | 'rework' | 'rejected';

export interface RunRow {
  id: string;
  businessId: string;
  task: { id: string; key: string; title: string } | null;
  agent: { instanceId: string; name: string };
  model: string;
  status: RunStatus;
  cost: string;
  currency: string;
  durationSec: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  turns: number;
  attempt: number;
}

export interface RunToolCall {
  turn: number;
  callId: string;
  toolName: string;
  args: unknown;
  verdict: string | null;
  reason: string | null;
  isError: boolean;
  content: string;
  at: string | null;
  externalActionId: string | null;
  resumed: string | null;
}

export interface RunDetail extends RunRow {
  business: { id: string; name: string } | null;
  taskStatus: string | null;
  agentActorId: string | null;
  template: { id: string; name: string } | null;
  templateVersion: number | null;
  agentInstanceVersion: number;
  policyVersion: number | null;
  promptVersion: string;
  supervisionLevel: string | null;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  error: string | null;
  updatedAt: string;
  canCancel: boolean;
  provenance: { toolCalls: RunToolCall[]; contextSnapshot: unknown; finalDecision: unknown };
  artifacts: Array<{ id: string; type: string; version: number; status: ArtifactStatus; createdAt: string }>;
  externalActions: Array<{ id: string; toolRef: string; status: ExternalActionStatus; idempotencyKey: string; externalReference: string | null; attempt: number; reversible: boolean; error: string | null; createdAt: string }>;
  approvals: Array<{ id: string; kind: ApprovalKind; subjectType: string; risk: string; decision: ApprovalDecision | null; decidedAt: string | null; approver: string | null; channel: string | null; reason: string | null; requestedAt: string; expiresAt: string | null }>;
}

export interface RunListParams {
  businessId?: string;
  status?: RunStatus[];
  agentInstanceId?: string;
  taskId?: string;
  from?: string;
  to?: string;
  sort?: RunSort;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}
export interface RunListResult { rows: RunRow[]; total: number; page: number; pageSize: number }
export interface RunAgentOption { id: string; name: string; businessId: string | null; status: string }

function qs(params: Record<string, string | number | string[] | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, x));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const runsApi = {
  list: (p: RunListParams) => request<RunListResult>(`/runs${qs({ ...p })}`),
  agents: (businessId?: string) => request<RunAgentOption[]>(`/runs/agents${qs({ businessId })}`),
  get: (id: string) => request<RunDetail>(`/runs/${encodeURIComponent(id)}`),
  cancel: (id: string) => post<{ ok: true }>(`/runs/${encodeURIComponent(id)}/cancel`),
  replay: (id: string) => post<{ executionId: string; taskKey: string }>(`/runs/${encodeURIComponent(id)}/replay`),
};
